import React, { useState, useEffect } from "react";
import { ResetPasswordSchema } from "../../../components/Helpers";
import { Formik, Form } from "formik";
import { FaRegEyeSlash, FaEye, FaCheckCircle } from "react-icons/fa";
import { Link, useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import { useDispatch, useSelector } from "react-redux";
import {
  requestResetPassword,
  setResetStatePassword,
} from "../../../Redux/LoginState/LoginActionCreator";
import FormField from "../../../components/FormField";
import Uni_Logo from "../../../Assets/DataCloud.jpeg";
import FormButton from "../../../components/FormButton";
import Loader from "../../../components/Loader";

const validationRules = [
  {
    label: "At least 8 characters long",
    test: (password) => password.length >= 8,
  },
  {
    label: "Contains at least one uppercase letter",
    test: (password) => /[A-Z]/.test(password),
  },
  {
    label: "Contains at least one lowercase letter",
    test: (password) => /[a-z]/.test(password),
  },
  {
    label: "Contains at least one number",
    test: (password) => /[0-9]/.test(password),
  },
  {
    label: "Contains at least one special character (@, $, !, %, *, ?, &, #)",
    test: (password) => /[@$!%*?&#]/.test(password),
  },
];

const getStrength = (validCount) => {
  const total = validationRules.length;
  if (validCount === 0) return { label: "Very Weak", color: "text-red-500" };
  if (validCount <= total * 0.4) return { label: "Weak", color: "text-orange-500" };
  if (validCount <= total * 0.7) return { label: "Medium", color: "text-yellow-500" };
  if (validCount === total) return { label: "Very Strong", color: "text-green-600" };
  return { label: "Strong", color: "text-gray-500" };
};

const getBarColor = (validCount) => {
  const total = validationRules.length;
  if (validCount === 0) return "bg-red-500";
  if (validCount <= total * 0.4) return "bg-orange-500";
  if (validCount <= total * 0.7) return "bg-yellow-500";
  if (validCount === total) return "bg-green-600";
  return "bg-gray-300";
};

const PasswordValidation = ({ password }) => {
  const validations = validationRules.map(rule => ({
    label: rule.label,
    isValid: rule.test(password),
  }));

  const validCount = validations.filter(v => v.isValid).length;
  const strength = getStrength(validCount);
  const barColor = getBarColor(validCount);

  const [expanded, setExpanded] = useState(false);

  return (
    <div className="w-full">
      <div className="flex flex-col gap-1 mb-2">
        <div className="flex items-center justify-between">
          <span className={`text-xs font-medium ${strength.color}`}>{strength.label}</span>
          <button
            type="button"
            className="text-xs text-primary underline ml-2"
            onClick={() => setExpanded((prev) => !prev)}
            aria-expanded={expanded}
          >
            {expanded ? "Hide details" : "Show details"}
          </button>
        </div>
        <div className="w-full h-2 rounded bg-gray-200 overflow-hidden">
          <div
            className={`h-full transition-all duration-300 ${barColor}`}
            style={{
              width: `${(validCount / validationRules.length) * 100}%`,
            }}
          />
        </div>
      </div>
      {expanded && (
        <ul className="mt-2 flex flex-col gap-2">
          {validations.map((validation, idx) => (
            <li key={idx} className="flex items-center gap-2">
              <FaCheckCircle
                className={`w-5 h-5 ${
                  validation.isValid ? "text-green-500" : "text-gray-300"
                }`}
              />
              <span
                className={`${
                  validation.isValid ? "text-green-700 font-medium" : "text-gray-500"
                }`}
              >
                {validation.label}
              </span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

function ResetForm() {
  const [isPasswordVisible, setIsPasswordVisible] = useState(false);
  const [isConfirmPasswordVisible, setIsConfirmPasswordVisible] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const dispatch = useDispatch();
  const { token } = useParams();
  const navigate = useNavigate();
  
  const resetPasswordState = useSelector((state) => state.ResetPasswordReducer);

  const resetPassword = (values) => {
    dispatch(requestResetPassword(values));
  };

  useEffect(() => {
    if (resetPasswordState?.resetResponse && !resetPasswordState?.resetLoading) {
      setIsSubmitted(true);
      toast.success("Password Reset Successful");
    }
    if (resetPasswordState?.resetError && !resetPasswordState?.resetLoading) {
      toast.error(resetPasswordState?.resetError?.message?.message || resetPasswordState?.resetError?.message || resetPasswordState?.resetError || "Something went wrong");
    }
  }, [resetPasswordState]);
 
  return (
    <div className="w-full">
      {/* <Loader
        isLoading={resetPasswordState?.resetLoading}
      /> */}
      {isSubmitted ? (
        <div className="text-center py-8"> 
          <div className="mb-6">
            <img 
              src={Uni_Logo} 
              alt="Logo" 
              className="w-32 h-auto mx-auto transform hover:scale-105 transition-transform duration-300" 
            />
          </div>
          <div className="bg-white/10 backdrop-blur-lg rounded-xl p-6 border border-white/20">
            <p className="mb-4 text-gray-700">
              Password Reset Successful. Please{" "}
              <Link className="text-primary hover:underline" to={"/login"}>
                Click Here
              </Link>{" "}
              to login.
            </p>
          </div>
        </div>
      ) : (
        <Formik
          initialValues={{
            newPassword: "",
            confirmPassword: "",
            token: token,
          }}
          validationSchema={ResetPasswordSchema}
          onSubmit={(values) => {
            if (values.newPassword === values.confirmPassword) {
              resetPassword(values);
            } else {
              toast.error("Passwords do not match");
              dispatch(setResetStatePassword());
            }
          }}
        >
          {({ errors, touched, values, handleChange,isSubmitting }) => (
            <Form className="space-y-6">
              <div className="flex justify-center mb-6">
                <img 
                  src={Uni_Logo} 
                  alt="Logo" 
                  className="w-32 h-auto transform hover:scale-105 transition-transform duration-300" 
                />
              </div>

              <div className="space-y-4">
                <FormField
                  label="New Password"
                  type={isPasswordVisible ? "text" : "password"}
                  name="newPassword"
                  value={values.newPassword}
                  onChange={handleChange}
                  placeholder="Enter new password"
                  error={errors.newPassword}
                  touched={touched.newPassword}
                  required
                  className="bg-white/5 border-gray-600 text-black placeholder-gray-400 focus:border-primary focus:ring-2 focus:ring-primary/20 transition-all duration-200"
                  icon={
                    <button
                      type="button"
                      onClick={() => setIsPasswordVisible(!isPasswordVisible)}
                      className="text-gray-400 hover:text-gray-300 transition-colors duration-200"
                    >
                      {isPasswordVisible ? <FaEye /> : <FaRegEyeSlash />}
                    </button>
                  }
                />

                {/* Password Validation Component */}
                {values.newPassword && <PasswordValidation password={values.newPassword} />}

                <FormField
                  label="Confirm Password"
                  type={isConfirmPasswordVisible ? "text" : "password"}
                  name="confirmPassword"
                  value={values.confirmPassword}
                  onChange={handleChange}
                  placeholder="Confirm new password"
                  error={errors.confirmPassword}
                  touched={touched.confirmPassword}
                  required
                  className="bg-white/5 border-gray-600 text-black placeholder-gray-400 focus:border-primary focus:ring-2 focus:ring-primary/20 transition-all duration-200"
                  icon={
                    <button
                      type="button"
                      onClick={() => setIsConfirmPasswordVisible(!isConfirmPasswordVisible)}
                      className="text-gray-400 hover:text-gray-300 transition-colors duration-200"
                    >
                      {isConfirmPasswordVisible ? <FaEye /> : <FaRegEyeSlash />}
                    </button>
                  }
                />
              </div>

              <FormButton
                type="submit"
                fullWidth
                loading={isSubmitting || resetPasswordState.isLoading}
                disabled={isSubmitting || resetPasswordState.isLoading}
                className="bg-primary hover:bg-primaryDark text-white font-medium py-3 rounded-lg shadow-lg hover:shadow-xl transform hover:scale-[1.02] transition-all duration-200"
              >
                {isSubmitting || resetPasswordState.isLoading ? (
                  <span className="flex items-center justify-center">
                    Resetting password...
                  </span>
                ) : (
                  "Reset Password"
                )}
              </FormButton>
            </Form>
          )}
        </Formik>
      )}
    </div>
  );
}

export default ResetForm;
